/**
 * adminCustomDesignRequestsSlice — Redux Toolkit slice for the admin custom
 * design request list (SH-CDR-001, extended SH-DSVC-101).
 *
 * State shape
 * -----------
 * - items: CustomDesignRequestDTO[]              — current page of requests
 * - total/currentPage/perPage/lastPage            — pagination from the API
 * - loading: boolean                              — list fetch in progress
 * - error: AdminCustomDesignRequestsError | null   — last error (full platform envelope)
 * - currentRequest: CustomDesignRequestDTO | null  — request open in the detail panel
 * - mutating: boolean                              — status-update / assign in progress
 * - quoting: boolean                               — create-quote request in progress
 * - lastCreatedQuote: CreateQuoteFromDesignRequestResponseData | null
 *     — result of the most recent successful create-quote call, so the
 *       console container can navigate to the quote editor without a
 *       second fetch.
 *
 * Per `.claude/rules/frontend-data-flow.md` §1, capsules NEVER import this
 * slice; only page-level containers do. Per §68 of the design doc, this is
 * the ONLY admin design-request slice — no parallel `designServicesSlice`.
 */
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import {
  assignCustomDesignRequest,
  createQuoteFromDesignRequest,
  fetchCustomDesignRequest,
  fetchCustomDesignRequests,
  updateCustomDesignRequestStatus,
} from '@dfq/shared/api/customDesignRequests';
import { isApiError } from '@dfq/shared/api/_envelope';
import type {
  CreateQuoteFromDesignRequestResponseData,
  CustomDesignRequestDTO,
  FetchCustomDesignRequestsParams,
  CustomDesignRequestStatus,
} from '@dfq/shared/types/customDesignRequest';

// ---------------------------------------------------------------------------
// Error shape — preserves the full platform error envelope
// ---------------------------------------------------------------------------

export interface AdminCustomDesignRequestsError {
  message: string;
  severity?: string;
  errors?: Record<string, string[]>;
  errorCode?: string;
  status: number;
}

// ---------------------------------------------------------------------------
// State
// ---------------------------------------------------------------------------

export interface AdminCustomDesignRequestsState {
  items: CustomDesignRequestDTO[];
  total: number;
  currentPage: number;
  perPage: number;
  lastPage: number;
  loading: boolean;
  error: AdminCustomDesignRequestsError | null;
  currentRequest: CustomDesignRequestDTO | null;
  mutating: boolean;
  quoting: boolean;
  lastCreatedQuote: CreateQuoteFromDesignRequestResponseData | null;
}

const initialState: AdminCustomDesignRequestsState = {
  items: [],
  total: 0,
  currentPage: 1,
  perPage: 25,
  lastPage: 1,
  loading: false,
  error: null,
  currentRequest: null,
  mutating: false,
  quoting: false,
  lastCreatedQuote: null,
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function toError(err: unknown, fallback: string): AdminCustomDesignRequestsError {
  if (isApiError(err)) {
    return {
      message: err.message,
      severity: err.severity,
      errors: err.errors,
      errorCode: err.errorCode,
      status: err.status,
    };
  }
  const msg = err instanceof Error ? err.message : fallback;
  return { message: msg, status: 0 };
}

/**
 * The status-update endpoint only ever changes `status` (see
 * `UpdateCustomDesignRequestStatusAction::buildDto` — it calls
 * `$existing->with(['status' => ...])`), but its response DTO can come back
 * with relations the repository didn't eager-load on this path (`files: []`,
 * `assignedUserName: null`) even though the row still has them. Merging only
 * `status`/`updatedAt` onto the existing record — rather than replacing it
 * wholesale — keeps `files`/`assignedUserName`/etc. from disappearing from
 * the UI after a status change.
 */
function mergeStatusUpdate(
  existing: CustomDesignRequestDTO,
  response: CustomDesignRequestDTO,
): CustomDesignRequestDTO {
  return { ...existing, status: response.status, updatedAt: response.updatedAt };
}

// ---------------------------------------------------------------------------
// Thunks
// ---------------------------------------------------------------------------

export const fetchAdminCustomDesignRequestsThunk = createAsyncThunk(
  'adminCustomDesignRequests/fetchList',
  async (params: FetchCustomDesignRequestsParams = {}, { rejectWithValue }) => {
    try {
      return await fetchCustomDesignRequests(params);
    } catch (err) {
      return rejectWithValue(toError(err, 'Failed to fetch custom design requests'));
    }
  },
);

export const fetchAdminCustomDesignRequestThunk = createAsyncThunk(
  'adminCustomDesignRequests/fetchOne',
  async (id: number, { rejectWithValue }) => {
    try {
      return await fetchCustomDesignRequest(id);
    } catch (err) {
      return rejectWithValue(toError(err, 'Failed to fetch custom design request'));
    }
  },
);

export const updateAdminCustomDesignRequestStatusThunk = createAsyncThunk(
  'adminCustomDesignRequests/updateStatus',
  async ({ id, status }: { id: number; status: CustomDesignRequestStatus }, { rejectWithValue }) => {
    try {
      return await updateCustomDesignRequestStatus(id, status);
    } catch (err) {
      return rejectWithValue(toError(err, 'Failed to update custom design request status'));
    }
  },
);

export const assignAdminCustomDesignRequestThunk = createAsyncThunk(
  'adminCustomDesignRequests/assign',
  async ({ id, userId }: { id: number; userId: number | null }, { rejectWithValue }) => {
    try {
      return await assignCustomDesignRequest(id, userId);
    } catch (err) {
      return rejectWithValue(toError(err, 'Failed to assign custom design request'));
    }
  },
);

export const createQuoteFromAdminCustomDesignRequestThunk = createAsyncThunk(
  'adminCustomDesignRequests/createQuote',
  async (id: number, { rejectWithValue }) => {
    try {
      return await createQuoteFromDesignRequest(id);
    } catch (err) {
      return rejectWithValue(toError(err, 'Failed to create quote from custom design request'));
    }
  },
);

// ---------------------------------------------------------------------------
// Slice
// ---------------------------------------------------------------------------

const adminCustomDesignRequestsSlice = createSlice({
  name: 'adminCustomDesignRequests',
  initialState,
  reducers: {
    clearCurrentCustomDesignRequest(state) {
      state.currentRequest = null;
      state.error = null;
      state.lastCreatedQuote = null;
    },
    clearError(state) {
      state.error = null;
    },
    clearLastCreatedQuote(state) {
      state.lastCreatedQuote = null;
    },
  },
  extraReducers: (builder) => {
    // fetchAdminCustomDesignRequestsThunk (list)
    builder
      .addCase(fetchAdminCustomDesignRequestsThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAdminCustomDesignRequestsThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload.customDesignRequests;
        state.total = action.payload.pagination.total;
        state.currentPage = action.payload.pagination.page;
        state.perPage = action.payload.pagination.per_page;
        state.lastPage = action.payload.pagination.last_page;
      })
      .addCase(fetchAdminCustomDesignRequestsThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = (action.payload as AdminCustomDesignRequestsError | undefined) ?? {
          message: action.error.message ?? 'Failed to fetch custom design requests',
          status: 0,
        };
      });

    // fetchAdminCustomDesignRequestThunk (single)
    builder
      .addCase(fetchAdminCustomDesignRequestThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAdminCustomDesignRequestThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.currentRequest = action.payload;
      })
      .addCase(fetchAdminCustomDesignRequestThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = (action.payload as AdminCustomDesignRequestsError | undefined) ?? {
          message: action.error.message ?? 'Failed to fetch custom design request',
          status: 0,
        };
      });

    // updateAdminCustomDesignRequestStatusThunk
    builder
      .addCase(updateAdminCustomDesignRequestStatusThunk.pending, (state) => {
        state.mutating = true;
        state.error = null;
      })
      .addCase(updateAdminCustomDesignRequestStatusThunk.fulfilled, (state, action) => {
        state.mutating = false;
        state.currentRequest =
          state.currentRequest && state.currentRequest.id === action.payload.id
            ? mergeStatusUpdate(state.currentRequest, action.payload)
            : action.payload;
        const idx = state.items.findIndex((r) => r.id === action.payload.id);
        if (idx !== -1) {
          state.items[idx] = mergeStatusUpdate(state.items[idx], action.payload);
        }
      })
      .addCase(updateAdminCustomDesignRequestStatusThunk.rejected, (state, action) => {
        state.mutating = false;
        state.error = (action.payload as AdminCustomDesignRequestsError | undefined) ?? {
          message: action.error.message ?? 'Failed to update custom design request status',
          status: 0,
        };
      });

    // assignAdminCustomDesignRequestThunk
    builder
      .addCase(assignAdminCustomDesignRequestThunk.pending, (state) => {
        state.mutating = true;
        state.error = null;
      })
      .addCase(assignAdminCustomDesignRequestThunk.fulfilled, (state, action) => {
        state.mutating = false;
        state.currentRequest = action.payload;
        const idx = state.items.findIndex((r) => r.id === action.payload.id);
        if (idx !== -1) {
          state.items[idx] = action.payload;
        }
      })
      .addCase(assignAdminCustomDesignRequestThunk.rejected, (state, action) => {
        state.mutating = false;
        state.error = (action.payload as AdminCustomDesignRequestsError | undefined) ?? {
          message: action.error.message ?? 'Failed to assign custom design request',
          status: 0,
        };
      });

    // createQuoteFromAdminCustomDesignRequestThunk
    builder
      .addCase(createQuoteFromAdminCustomDesignRequestThunk.pending, (state) => {
        state.quoting = true;
        state.error = null;
        // Clear any prior result up front so a container can never navigate
        // to a stale quote if this call fails or targets a different request.
        state.lastCreatedQuote = null;
      })
      .addCase(createQuoteFromAdminCustomDesignRequestThunk.fulfilled, (state, action) => {
        state.quoting = false;
        state.lastCreatedQuote = action.payload;
        state.currentRequest = action.payload.customDesignRequest;
        const idx = state.items.findIndex((r) => r.id === action.payload.customDesignRequest.id);
        if (idx !== -1) {
          state.items[idx] = action.payload.customDesignRequest;
        }
      })
      .addCase(createQuoteFromAdminCustomDesignRequestThunk.rejected, (state, action) => {
        state.quoting = false;
        state.error = (action.payload as AdminCustomDesignRequestsError | undefined) ?? {
          message: action.error.message ?? 'Failed to create quote from custom design request',
          status: 0,
        };
      });
  },
});

export const {
  clearCurrentCustomDesignRequest,
  clearError: clearAdminCustomDesignRequestError,
  clearLastCreatedQuote: clearAdminCustomDesignRequestLastCreatedQuote,
} = adminCustomDesignRequestsSlice.actions;

export const adminCustomDesignRequestsReducer = adminCustomDesignRequestsSlice.reducer;

// ---------------------------------------------------------------------------
// Selectors
// ---------------------------------------------------------------------------

export interface AdminCustomDesignRequestsRootState {
  adminCustomDesignRequests: AdminCustomDesignRequestsState;
}

export const selectAdminCustomDesignRequests = (state: AdminCustomDesignRequestsRootState): CustomDesignRequestDTO[] =>
  state.adminCustomDesignRequests.items;

export const selectAdminCustomDesignRequestsLoading = (state: AdminCustomDesignRequestsRootState): boolean =>
  state.adminCustomDesignRequests.loading;

export const selectAdminCustomDesignRequestsError = (
  state: AdminCustomDesignRequestsRootState,
): AdminCustomDesignRequestsError | null => state.adminCustomDesignRequests.error;

export const selectCurrentAdminCustomDesignRequest = (
  state: AdminCustomDesignRequestsRootState,
): CustomDesignRequestDTO | null => state.adminCustomDesignRequests.currentRequest;

export const selectAdminCustomDesignRequestsMutating = (state: AdminCustomDesignRequestsRootState): boolean =>
  state.adminCustomDesignRequests.mutating;

export const selectAdminCustomDesignRequestsTotal = (state: AdminCustomDesignRequestsRootState): number =>
  state.adminCustomDesignRequests.total;

export const selectAdminCustomDesignRequestsPage = (state: AdminCustomDesignRequestsRootState): number =>
  state.adminCustomDesignRequests.currentPage;

export const selectAdminCustomDesignRequestsPerPage = (state: AdminCustomDesignRequestsRootState): number =>
  state.adminCustomDesignRequests.perPage;

export const selectAdminCustomDesignRequestsLastPage = (state: AdminCustomDesignRequestsRootState): number =>
  state.adminCustomDesignRequests.lastPage;

export const selectAdminCustomDesignRequestsQuoting = (state: AdminCustomDesignRequestsRootState): boolean =>
  state.adminCustomDesignRequests.quoting;

export const selectAdminCustomDesignRequestsLastCreatedQuote = (
  state: AdminCustomDesignRequestsRootState,
): CreateQuoteFromDesignRequestResponseData | null => state.adminCustomDesignRequests.lastCreatedQuote;
